import React, { useState } from 'react';
import { Calendar } from 'lucide-react';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser } from '@/store/account/selector';
import { InlineWidget } from 'react-calendly';
import { Modal } from 'antd';
import { getResellerData } from '@/store/reseller/selector';
import { LocalStorageKeys } from 'src/enums/ELocalStorage';
import LocalStorageUtility from '@/utils/localStorage';
import { DEFAULT_CALENDLY_LINK } from 'src/constants';

const WelcomeStage: React.FC<{ handleNext: () => void }> = ({ handleNext }) => {
	const loggedInUser = useAppSelector(getLoggedInUser);

	const [showCalendly, setShowCalendly] = useState(false);
	const [hasClaimedCall, setHasClaimedCall] = useState(() => {
		return LocalStorageUtility.getLocalData(LocalStorageKeys.HAS_CLAIMED_FREE_CALL_KEY) === 'true';
	});

	const isResellerClient =
		loggedInUser?.resellerClient ||
		!!loggedInUser?.resellerId ||
		loggedInUser?.userType === 'SubAccount';

	const currentReseller = useAppSelector(getResellerData);

	const handleClaimCall = () => {
		setHasClaimedCall(true);
		LocalStorageUtility.setLocalData(LocalStorageKeys.HAS_CLAIMED_FREE_CALL_KEY, 'true');
		setShowCalendly(true);
	};

	return (
		<div className="flex-1 flex flex-col h-full">
			<div className="mb-6 text-center">
				<div className="text-2xl lg:text-3xl font-bold text-blue-600 mb-2">
					Welcome to{' '}
					{!isResellerClient ? 'Frontdesk' : currentReseller?.businessName || 'your Dashboard'}!
				</div>
				<p className="text-sm lg:text-base text-gray-600">
					Your AI Receptionist is live and ready.
				</p>
				{!isResellerClient && (
					<p className="text-sm text-gray-700 mb-0">
						Start using it now and get expert help for free (limited-time offer).
					</p>
				)}
			</div>

			{/* Special Offer Card */}
			{!isResellerClient ? (
				<>
					<div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 lg:px-20 mb-6 w-fit mx-auto">
						{/* Header with icon and stars */}
						<div className="text-center mb-4">
							<h3 className="text-xl font-bold text-emerald-800 mb-2 flex items-center justify-center gap-2">
								🎁 1:1 Onboarding Call
							</h3>
						</div>

						{/* Pricing */}
						<div className="text-center mb-4">
							<div className="flex items-center justify-center gap-2">
								<span className="text-xl font-medium text-gray-400 line-through">$299</span>
								<span className="text-xl font-bold text-emerald-700">
									Free (
									{new Date().toLocaleDateString('en-US', {
										month: 'long',
									})}{' '}
									only)
								</span>
							</div>
						</div>

						{/* CTA Button */}
						<div className="w-full flex justify-center">
							<button
								onClick={handleClaimCall}
								className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
							>
								<Calendar className="w-5 h-5" />
								Claim Free Call
							</button>
						</div>
					</div>
					<div className="w-full flex justify-center">
						<button
							onClick={handleNext}
							className={`w-fit font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 ${
								hasClaimedCall
									? 'bg-blue-600 hover:bg-blue-700 text-white'
									: 'border hover:bg-gray-50'
							}`}
						>
							{hasClaimedCall ? 'Review My Receptionist Now' : 'No thanks, skip to setup'} &rarr;
						</button>
					</div>
				</>
			) : (
				<div className="w-full flex justify-center">
					<button
						onClick={handleNext}
						className="w-fit font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
					>
						Review My Receptionist Now &rarr;
					</button>
				</div>
			)}

			{/* Embedded Calendly Calendar */}

			{!isResellerClient && showCalendly && (
				<Modal
					open={showCalendly}
					closable={true}
					onCancel={() => {
						setShowCalendly(false);
					}}
					footer={null}
					width={1000}
					className="[&_.ant-modal-content]:rounded-2xl [&_.ant-modal-content]:overflow-hidden [&_.ant-modal-content]:p-0 [&_.ant-modal-body]:p-0 [&_.ant-modal-content]:max-h-[90vh] [&_.ant-modal-content]:flex [&_.ant-modal-content]:flex-col"
					styles={{
						mask: {
							backdropFilter: 'blur(8px)',
							backgroundColor: 'rgba(0, 0, 0, 0.2)',
						},
					}}
				>
					<InlineWidget url={DEFAULT_CALENDLY_LINK} />
				</Modal>
			)}
		</div>
	);
};

export default WelcomeStage;
